import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  fetchSubscriptionPlans,
  priceForPeriod,
  type SubscriptionPlan,
} from '@/lib/subscriptionPlans';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Enregistrer un paiement d'abonnement manuel
// (espèces, virement, paiement encaissé hors Zifek…)
// On choisit l'utilisateur + la formule qu'il a prise, et la ligne
// est créée directement en statut « payé ».
// ─────────────────────────────────────────────────────────────

interface UserOption {
  id: number;
  name: string | null;
  nomcommerce: string | null;
  email: string | null;
  monaie: string | null;
}

interface MarkPaidModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const CURRENCIES = ['MAD', 'DHS', 'EUR', 'USD', 'XOF', 'XAF', 'CAD'];

const METHODS: { value: string; label: string }[] = [
  { value: 'espece', label: 'Espèces' },
  { value: 'virement', label: 'Virement bancaire' },
  { value: 'feexpay', label: 'FeexPay (Mobile Money)' },
  { value: 'carte', label: 'Carte bancaire' },
  { value: 'autre', label: 'Autre' },
];

const inputClass =
  'w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300';

function displayName(u: UserOption): string {
  return u.nomcommerce || u.name || u.email || `Utilisateur #${u.id}`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function MarkPaidModal({ open, onClose, onSaved }: MarkPaidModalProps) {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [planId, setPlanId] = useState<number | null>(null);
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('MAD');
  const [method, setMethod] = useState('espece');
  const [paidOn, setPaidOn] = useState(todayISO());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Chargement des utilisateurs + formules à l'ouverture.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setLoadingUsers(true);
      setLoadError('');
      try {
        const [usersRes, planList] = await Promise.all([
          supabase
            .from('users')
            .select('id, name, nomcommerce, email, monaie')
            .order('name', { ascending: true })
            .limit(500),
          fetchSubscriptionPlans(),
        ]);
        if (cancelled) return;
        if (usersRes.error) throw usersRes.error;
        setUsers((usersRes.data || []) as UserOption[]);
        setPlans(planList);
        if (planList.length > 0 && planId == null) {
          const first = planList.find((p) => !p.personnalise) || planList[0];
          setPlanId(first.id);
          setAmount(String(priceForPeriod(first, 'monthly')));
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Impossible de charger les utilisateurs.');
        }
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };

    void load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedUser = useMemo(
    () => users.find((u) => Number(u.id) === Number(userId)) || null,
    [users, userId],
  );

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === planId) || null,
    [plans, planId],
  );

  // Prix par défaut : prix de la formule dans la période choisie.
  useEffect(() => {
    if (!selectedPlan) return;
    setAmount(String(priceForPeriod(selectedPlan, period)));
  }, [selectedPlan, period]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? users.filter((u) =>
        `${u.name || ''} ${u.nomcommerce || ''} ${u.email || ''} ${u.id}`.toLowerCase().includes(q))
      : users;
    return list.slice(0, 60);
  }, [users, search]);

  if (!open) return null;

  const pickUser = (user: UserOption) => {
    setUserId(Number(user.id));
    setSearch(displayName(user));
    if (user.monaie) setCurrency(user.monaie.toUpperCase());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!userId) {
      setError('Choisissez l’utilisateur concerné.');
      return;
    }
    if (!selectedPlan) {
      setError('Choisissez la formule souscrite.');
      return;
    }
    const price = Number(amount);
    if (!Number.isFinite(price) || price < 0) {
      setError('Indiquez un montant valide.');
      return;
    }

    setSaving(true);
    try {
      const start = paidOn ? new Date(paidOn) : new Date();
      const next = new Date(start);
      const months = (period === 'yearly' ? 12 : 1) + Math.max(0, Number(selectedPlan.moisGratuits) || 0);
      next.setMonth(next.getMonth() + months);
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      const reference = `MAN-${Date.now().toString().slice(-9)}`;

      const { error: insertError } = await supabase.from('package_subscriptions').insert({
        user_id: userId,
        plan: selectedPlan.nom,
        period,
        prix: price,
        monaie: currency,
        start_date: fmt(start),
        next_billing_date: fmt(next),
        status: 'paid',
        payment_method: method,
        last_payment_id: reference,
        note: `Paiement enregistré manuellement par le SuperAdmin${note.trim() ? ` — ${note.trim()}` : ''}`,
      });
      if (insertError) throw insertError;

      setUserId(null);
      setSearch('');
      setNote('');
      setPaidOn(todayISO());
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div
        className="absolute inset-0 bg-foreground-950/50"
        onClick={onClose}
        role="presentation"
      ></div>
      <div className="relative w-full max-w-xl bg-background-50 border border-background-200/70 rounded-xl p-5 my-8">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-line text-accent-700"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold font-heading text-foreground-950">Enregistrer un paiement</h2>
              <p className="text-xs text-foreground-500">Choisissez l’utilisateur et la formule qu’il a prise.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Fermer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {loadError && (
          <div className="mb-3 px-3 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
            <i className="ri-error-warning-line"></i>{loadError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Utilisateur */}
          <div>
            <label htmlFor="mp-user" className="block text-[11px] font-semibold text-foreground-600 mb-1">
              Utilisateur <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                id="mp-user"
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setUserId(null); }}
                placeholder="Rechercher par nom, boutique, email ou ID…"
                className={`${inputClass} pl-9`}
                autoComplete="off"
              />
            </div>
            <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-background-200/70 bg-background-50">
              {loadingUsers ? (
                <p className="px-3 py-3 text-xs text-foreground-400 flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>Chargement des utilisateurs…
                </p>
              ) : filteredUsers.length === 0 ? (
                <p className="px-3 py-3 text-xs text-foreground-400">Aucun utilisateur trouvé.</p>
              ) : (
                filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => pickUser(u)}
                    className={`w-full text-left px-3 py-2 border-b border-background-200/50 last:border-0 transition-colors cursor-pointer ${
                      Number(userId) === Number(u.id) ? 'bg-primary-50/60' : 'hover:bg-background-100'
                    }`}
                  >
                    <span className="block text-xs font-medium text-foreground-900 truncate">{displayName(u)}</span>
                    <span className="block text-[11px] text-foreground-400 truncate">
                      {u.email || '—'} · #{u.id}
                    </span>
                  </button>
                ))
              )}
            </div>
            {selectedUser && (
              <p className="mt-1.5 text-[11px] text-accent-700 flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                Sélectionné : {displayName(selectedUser)} (#{selectedUser.id})
              </p>
            )}
          </div>

          {/* Formule + période */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mp-plan" className="block text-[11px] font-semibold text-foreground-600 mb-1">
                Formule souscrite <span className="text-red-500">*</span>
              </label>
              <select
                id="mp-plan"
                value={planId ?? ''}
                onChange={(e) => setPlanId(Number(e.target.value) || null)}
                className={`${inputClass} cursor-pointer`}
              >
                <option value="">— Choisir —</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom}{p.personnalise ? ' (sur devis)' : ` — ${p.prix} MAD/mois`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className="block text-[11px] font-semibold text-foreground-600 mb-1">Période</span>
              <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit">
                {(['monthly', 'yearly'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={`px-4 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                      period === p ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {p === 'monthly' ? 'Mensuel' : 'Annuel'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Montant + devise */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mp-amount" className="block text-[11px] font-semibold text-foreground-600 mb-1">
                Montant encaissé <span className="text-red-500">*</span>
              </label>
              <input
                id="mp-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputClass}
              />
              <p className="text-[10px] text-foreground-400 mt-1">Les prix des formules sont exprimés en MAD.</p>
            </div>
            <div>
              <label htmlFor="mp-currency" className="block text-[11px] font-semibold text-foreground-600 mb-1">Devise</label>
              <select
                id="mp-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={`${inputClass} cursor-pointer`}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Moyen + date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mp-method" className="block text-[11px] font-semibold text-foreground-600 mb-1">Moyen de paiement</label>
              <select
                id="mp-method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className={`${inputClass} cursor-pointer`}
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="mp-date" className="block text-[11px] font-semibold text-foreground-600 mb-1">Date du paiement</label>
              <input
                id="mp-date"
                type="date"
                value={paidOn}
                onChange={(e) => setPaidOn(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="mp-note" className="block text-[11px] font-semibold text-foreground-600 mb-1">Note (facultatif)</label>
            <input
              id="mp-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 200))}
              placeholder="Ex. réglé en espèces à la boutique"
              className={inputClass}
            />
          </div>

          {selectedPlan && (
            <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-background-100/70 text-xs">
              <span className="text-foreground-600">
                {selectedPlan.nom} · {period === 'monthly' ? 'mensuel' : 'annuel'}
                {selectedPlan.moisGratuits > 0 ? ` · +${selectedPlan.moisGratuits} mois offert(s)` : ''}
              </span>
              <span className="font-bold text-primary-600 whitespace-nowrap">
                {(Number(amount) || 0).toLocaleString('fr-FR')} {currency}
              </span>
            </div>
          )}

          {error && (
            <div className="px-3 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-center gap-2">
              <i className="ri-error-warning-line"></i>{error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || loadingUsers}
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</>
              ) : (
                <><i className="ri-check-line"></i>Marquer comme payé</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}